import { useState, useCallback } from 'react';
import { AutoModel, AutoProcessor, RawImage } from '@huggingface/transformers';
import { toast } from 'sonner';

let modelInstance: any = null;
let processorInstance: any = null;
let modelLoading = false;

interface BackgroundRemovalOptions {
  backgroundColor?: string;
  customBackground?: File | null;
  addShadow?: boolean;
  featherEdges?: number;
}

interface ProcessedImage {
  id: string;
  original: string;
  processed: string;
  blob: Blob;
}

async function loadModel() {
  if (modelInstance && processorInstance) return { model: modelInstance, processor: processorInstance };
  if (modelLoading) {
    // Wait for existing load
    while (modelLoading) await new Promise(r => setTimeout(r, 200));
    return { model: modelInstance, processor: processorInstance };
  }
  
  modelLoading = true;
  try {
    console.log('🔄 Loading RMBG-1.4 background removal model...');
    const model = await AutoModel.from_pretrained('briaai/RMBG-1.4', {
      // @ts-ignore - RMBG-1.4 uses custom model type
      config: { model_type: 'custom' } as any,
    });
    const processor = await AutoProcessor.from_pretrained('briaai/RMBG-1.4', {
      config: {
        do_normalize: true,
        do_pad: false,
        do_rescale: true,
        do_resize: true,
        image_mean: [0.5, 0.5, 0.5],
        image_std: [1, 1, 1],
        size: { width: 1024, height: 1024 },
      }
    });
    modelInstance = model;
    processorInstance = processor;
    console.log('✅ RMBG-1.4 model loaded');
    return { model, processor };
  } finally {
    modelLoading = false;
  }
}

async function removeBackground(imageElement: HTMLImageElement): Promise<Blob> {
  const { model, processor } = await loadModel();
  
  // Load image for the model
  const image = await RawImage.fromURL(imageElement.src);
  
  // Process image
  const { pixel_values } = await processor(image);
  const { output } = await model({ input: pixel_values });
  
  // Post-process mask
  const maskData = await RawImage.fromTensor(output[0].mul(255).to('uint8')).resize(image.width, image.height);
  
  // Apply mask to original image
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(imageElement, 0, 0, image.width, image.height);
  
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixelData = imageData.data;
  
  for (let i = 0; i < maskData.data.length; i++) {
    pixelData[i * 4 + 3] = maskData.data[i]; // Set alpha from mask
  }
  
  ctx.putImageData(imageData, 0, 0);
  
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Failed to create blob')),
      'image/png',
      1.0
    );
  });
}

async function applyBackgroundOptions(
  bgRemovedBlob: Blob,
  options: BackgroundRemovalOptions
): Promise<Blob> {
  const img = await loadImage(bgRemovedBlob);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  
  if (options.backgroundColor) {
    ctx.fillStyle = options.backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  } else if (options.customBackground) {
    const bgImg = await loadImage(options.customBackground);
    ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
  }
  
  if (options.addShadow) {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 20;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 10;
  }
  
  if (options.featherEdges && options.featherEdges > 0) {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext('2d');
    if (tempCtx) {
      tempCtx.drawImage(img, 0, 0);
      tempCtx.filter = `blur(${options.featherEdges}px)`;
      ctx.drawImage(tempCanvas, 0, 0);
    }
  } else {
    ctx.drawImage(img, 0, 0);
  }
  
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Failed to create blob')),
      'image/png',
      1.0
    );
  });
}

function loadImage(file: Blob | File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export function useBackgroundRemoval() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processedImages, setProcessedImages] = useState<ProcessedImage[]>([]);

  const processImage = useCallback(async (
    file: File,
    options: BackgroundRemovalOptions = {}
  ): Promise<ProcessedImage | null> => {
    try {
      const img = await loadImage(file);
      const bgRemovedBlob = await removeBackground(img);
      const finalBlob = await applyBackgroundOptions(bgRemovedBlob, options);
      
      return {
        id: crypto.randomUUID(),
        original: URL.createObjectURL(file),
        processed: URL.createObjectURL(finalBlob),
        blob: finalBlob,
      };
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error(`Failed to process image: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return null;
    }
  }, []);

  const processBatch = useCallback(async (
    files: File[],
    options: BackgroundRemovalOptions = {}
  ) => {
    setIsProcessing(true);
    setProgress(0);
    setProcessedImages([]);
    
    const results: ProcessedImage[] = [];
    
    for (let i = 0; i < files.length; i++) {
      toast.loading(`Processing image ${i + 1} of ${files.length}...`, { id: 'bg-removal-progress' });
      
      const result = await processImage(files[i], options);
      if (result) {
        results.push(result);
        setProcessedImages((prev) => [...prev, result]);
      }
      
      setProgress(((i + 1) / files.length) * 100);
    }
    
    toast.success(`Successfully processed ${results.length} of ${files.length} images`, { id: 'bg-removal-progress' });
    setIsProcessing(false);
    
    return results;
  }, [processImage]);

  const clearResults = useCallback(() => {
    processedImages.forEach(img => {
      URL.revokeObjectURL(img.original);
      URL.revokeObjectURL(img.processed);
    });
    setProcessedImages([]);
    setProgress(0);
  }, [processedImages]);

  return {
    isProcessing,
    progress,
    processedImages,
    processImage,
    processBatch,
    clearResults,
  };
}
